<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import Box from '@uiKit/elements/Box.vue'
import Card from '@uiKit/components/Card.vue'
import EmptyState from '@uiKit/components/EmptyState.vue'
import FilterCardRow from '@uiKit/components/FilterCardRow.vue'
import ListTableCard, { type ListTableState } from '@uiKit/components/ListTableCard.vue'
import { routes } from '@app/router/routes'
import { debounce } from '@shared/utils'
import PropertyListRow from '../components/list/PropertyListRow.vue'
import { usePropertiesPageQuery } from '../api/usePropertiesPageQuery'
import { usePropertyStatusCountsQuery } from '../api/usePropertyStatusCountsQuery'
import { PROPERTIES_PAGE_LIMIT } from '../constants/propertyLifecycle'
import {
  PROPERTY_GRID_TEMPLATE,
  PROPERTY_LIST_FILTERS,
  PROPERTY_SEARCH_DEBOUNCE_MS,
  PROPERTY_TABLE_COLUMNS,
  type PropertyListFilterCard,
} from '../constants/propertyListTable'
import type {
  PropertyListBucket,
  PropertyListFilters,
  PropertyListPage,
} from '../types/propertyTypes'

// Smart container for the Properties table (FSD 3.3.1), laid out like the Sell
// Requests and Marketplace lists: five quick-filter cards with counts, then the
// shared list card — toolbar, table and pager. Filter and search run server-side, so totals and pages
// are right. A seller cannot create a property here — properties only appear once
// an admin promotes an approved sell request.
const router = useRouter()

const page = ref(1)
const bucket = ref<PropertyListBucket | null>(null)
const searchTerm = ref('')
const appliedSearch = ref('')

const applySearch = debounce((term: string) => {
  appliedSearch.value = term.trim()
}, PROPERTY_SEARCH_DEBOUNCE_MS)
watch(searchTerm, applySearch)

const filters = computed<PropertyListFilters>(() => ({
  bucket: bucket.value,
  search: appliedSearch.value,
}))

// A new filter or search starts from the first page.
watch(filters, () => {
  page.value = 1
})

const query = usePropertiesPageQuery(page, filters)
const countsQuery = usePropertyStatusCountsQuery()

const rows = computed(() => query.data.value?.rows ?? [])
// The last pagination that loaded, so a failed page fetch does not strand the
// seller without a pager.
const lastPagination = ref<PropertyListPage['pagination']>()
const pagination = computed(() => query.data.value?.pagination ?? lastPagination.value)

watch(pagination, (meta) => {
  if (meta) lastPagination.value = meta
  if (meta && meta.totalPages > 0 && page.value > meta.totalPages) page.value = meta.totalPages
})

const filterLabel = computed(
  () => PROPERTY_LIST_FILTERS.find((filter) => filter.bucket === bucket.value)?.label ?? '',
)
const selectedFilterKey = computed({
  get: () => PROPERTY_LIST_FILTERS.find((filter) => filter.bucket === bucket.value)?.key ?? 'all',
  set: (key: PropertyListFilterCard['key']) => {
    bucket.value = PROPERTY_LIST_FILTERS.find((filter) => filter.key === key)?.bucket ?? null
  },
})
const filterCards = computed(() =>
  PROPERTY_LIST_FILTERS.map((filter) => ({
    key: filter.key,
    label: filter.label,
    value: countsQuery.data.value?.[filter.key] ?? null,
  })),
)
const isFirstLoad = computed(() => query.isPending.value)
const hasError = computed(() => query.isError.value && rows.value.length === 0)
const isListEmpty = computed(
  () => !query.isPending.value && !query.isError.value && (pagination.value?.totalItems ?? 0) === 0,
)
const isFiltered = computed(() => bucket.value !== null || appliedSearch.value !== '')
// Only an unfiltered empty list means the seller has no properties at all.
const hasNoProperties = computed(() => isListEmpty.value && !isFiltered.value)
const hasNoMatches = computed(() => isListEmpty.value && isFiltered.value)

const listState = computed<ListTableState>(() => {
  if (isFirstLoad.value) return 'loading'
  if (hasError.value) return 'error'
  return hasNoMatches.value ? 'empty' : 'ready'
})
const tablePagination = computed(() =>
  pagination.value
    ? { ...pagination.value, page: page.value, pageSize: PROPERTIES_PAGE_LIMIT }
    : null,
)

function clearFilters() {
  bucket.value = null
  searchTerm.value = ''
  appliedSearch.value = ''
}

function goToDetail(id: string) {
  void router.push(routes.PROPERTY_DETAIL.replace(':id', id))
}

function goToSellRequests() {
  void router.push(routes.LISTINGS)
}
</script>

<template>
  <Box direction="col" gap="4">
    <Card v-if="hasNoProperties" variant="outlined" padding="6">
      <EmptyState
        icon="building"
        title="No properties yet"
        description="Once a sell request is approved, signed and set up by our team, the property appears here."
        primary-action-label="View sell requests"
        @primary-action="goToSellRequests"
      />
    </Card>

    <template v-else>
      <FilterCardRow
        v-model="selectedFilterKey"
        :cards="filterCards"
        :loading="countsQuery.isPending.value"
        group-label="Filter properties"
      />

      <ListTableCard
        v-model:search="searchTerm"
        :title="filterLabel"
        :state="listState"
        :columns="PROPERTY_TABLE_COLUMNS"
        :grid-template="PROPERTY_GRID_TEMPLATE"
        :pagination="tablePagination"
        item-label="property"
        items-label="properties"
        search-placeholder="Search by name, reference or location"
        error-message="We couldn't load your properties. Check your connection and try again."
        :is-search-disabled="isFirstLoad && !isFiltered"
        :is-refreshing="query.isPlaceholderData.value"
        @update:page="page = $event"
        @retry="query.refetch()"
      >
        <template #empty>
          <EmptyState
            size="compact"
            icon="search"
            title="No matching properties"
            description="None of your properties match this filter or search."
            secondary-action-label="Show all properties"
            @secondary-action="clearFilters"
          />
        </template>

        <PropertyListRow
          v-for="property in rows"
          :key="property.id"
          :property="property"
          @view="goToDetail"
        />
      </ListTableCard>
    </template>
  </Box>
</template>
